import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, Flex, HStack, Text, VStack } from '@bonusly/design-system';

import { useMsTeamsAuthorize, useMsTeamsDisconnect, useMsTeamsStatus } from '../../queries/ms-teams';
import { MsTeamsLogo } from './ms-teams-logo';

interface MsTeamsConnectCardProps {
	returnPath?: string;
}

export const MsTeamsConnectCard: FC<MsTeamsConnectCardProps> = ({ returnPath }) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'ms_teams' });
	const { data: status, isLoading: isLoadingStatus } = useMsTeamsStatus();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useMsTeamsAuthorize();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useMsTeamsDisconnect();
	const { track } = useAnalytics();

	const connectionStatus = status?.status;

	useEffect(() => {
		if (connectionStatus === 'connected' && sessionStorage.getItem('bizy_connecting_ms_teams')) {
			track(AnalyticsEvent.MsTeams.Connected);
			sessionStorage.removeItem('bizy_connecting_ms_teams');
		}
	}, [connectionStatus, track]);

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.MsTeams.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(returnPath ?? window.location.pathname);
			if (redirectUrl) {
				sessionStorage.setItem('bizy_connecting_ms_teams', 'true');
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, returnPath, track]);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.MsTeams.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	if (isLoadingStatus) return null;
	const isConnected = connectionStatus === 'connected';

	return (
		<Box py={3} borderBottom="1px solid" borderColor="border.soft">
			<Flex alignItems="center" justifyContent="space-between">
				<HStack spacing={3} align="center">
					<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center">
						<MsTeamsLogo width="24" height="24" />
					</Box>
					<VStack spacing={0} align="flex-start">
						<Text size="md" variant="strong" color="text.default">
							{t('name')}
						</Text>
						<Text size="sm" color="text.softer">
							{isConnected ? t('status.connected') : t('status.notConnected')}
						</Text>
					</VStack>
				</HStack>
				{isConnected ? (
					<Button size="md" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
						{t('disconnect')}
					</Button>
				) : (
					<Button size="md" colorScheme="primary" onClick={handleConnect} isLoading={isAuthorizing}>
						{t('connect')}
					</Button>
				)}
			</Flex>
		</Box>
	);
};
